import { useCallback, useEffect, useState } from "react";
import { Check } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import { useIsDesktop } from "@/hooks/useIsDesktop";

import { HeroWorld } from "./world/HeroWorld";
import type { HeroPropId } from "./world/heroWorldConfig";
import { SETTLE_SPRING } from "./SectionHeading";
import { WorldFallback } from "./world/WorldFallback";

/** Stage delays for the activity panel; stage 6 = fully resolved. */
const STAGE_DELAYS_MS = [400, 1300, 2300, 3300, 4300, 5200] as const;

interface ActivityPanelProps {
  t: TFunction<"home">;
  instant: boolean;
}

/**
 * The single detailed overlay: one plain instruction resolving into checked
 * steps, with the held work carrying an amber tag. Self-contained; never
 * touches the scene.
 */
function ActivityPanel({ t, instant }: ActivityPanelProps) {
  const [stage, setStage] = useState(instant ? STAGE_DELAYS_MS.length : 0);

  useEffect(() => {
    if (instant) return;
    const ids = STAGE_DELAYS_MS.map((delay, i) =>
      setTimeout(() => setStage(i + 1), delay),
    );
    return () => ids.forEach(clearTimeout);
  }, [instant]);

  const steps = ["stepOne", "stepTwo", "stepThree"] as const;

  return (
    <div className="rounded-2xl bg-white/95 p-5 shadow-[0_24px_50px_-24px_rgba(33,31,28,0.5)] backdrop-blur-sm">
      <p className="font-body text-[10px] font-bold tracking-[0.16em] text-home-ink/65 uppercase">
        {t("hero.panel.title")}
      </p>

      <AnimatePresence>
        {stage >= 1 && (
          <motion.p
            initial={instant ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={instant ? { duration: 0 } : SETTLE_SPRING}
            className="mt-2.5 rounded-xl rounded-bl-md bg-home-ink px-3.5 py-2.5 font-body text-[12px] leading-snug text-home-cream"
          >
            {t("hero.panel.instruction")}
          </motion.p>
        )}
      </AnimatePresence>

      <ul className="mt-3 space-y-2">
        {steps.map((key, i) => (
          <AnimatePresence key={key}>
            {stage >= i + 2 && (
              <motion.li
                initial={instant ? false : { opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={instant ? { duration: 0 } : SETTLE_SPRING}
                className="flex items-center gap-2.5 font-body text-[12px] font-medium text-home-ink/75"
              >
                <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-home-sky">
                  <Check className="h-2.5 w-2.5 text-home-ink" strokeWidth={3.5} />
                </span>
                {t(`hero.panel.${key}`)}
              </motion.li>
            )}
          </AnimatePresence>
        ))}
        <AnimatePresence>
          {stage >= 5 && (
            <motion.li
              initial={instant ? false : { opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={instant ? { duration: 0 } : SETTLE_SPRING}
              className="flex flex-wrap items-center gap-2 font-body text-[12px] font-medium text-home-ink"
            >
              <span className="rounded-full bg-home-butter px-2 py-0.5 text-[10px] font-bold whitespace-nowrap">
                {t("hero.panel.heldTag")}
              </span>
              {t("hero.panel.held")}
            </motion.li>
          )}
        </AnimatePresence>
      </ul>

      <AnimatePresence>
        {stage >= 6 && (
          <motion.p
            initial={instant ? false : { opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={instant ? { duration: 0 } : SETTLE_SPRING}
            className="mt-3 border-t border-home-ink/10 pt-3 font-body text-[12px] leading-snug font-bold text-home-ink"
          >
            {t("hero.panel.summary")}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}

export function HeroStage() {
  const { t } = useTranslation("home");
  const reduced = useReducedMotion() ?? false;
  const isDesktop = useIsDesktop();
  // The world ships on phones too; only reduced motion gets the static image.
  const interactive = !reduced;

  const [tooltip, setTooltip] = useState<{ id: HeroPropId; x: number; y: number } | null>(null);
  const [panelKey, setPanelKey] = useState(0);

  const handleHover = useCallback(
    (id: HeroPropId | null, x?: number, y?: number) => {
      setTooltip(id && x !== undefined && y !== undefined ? { id, x, y } : null);
    },
    [],
  );

  /* The calm zone: only the words, on solid page off-white. */
  const textZone = (
    <div className="pointer-events-none relative z-10 mx-auto max-w-3xl px-5 pt-6 pb-8 text-center md:pt-8 md:pb-10">
      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={SETTLE_SPRING}
        className="mx-auto max-w-3xl font-display text-[clamp(32px,3.9vw,50px)] leading-[1.06] font-semibold tracking-tight text-home-ink"
      >
        {t("hero.title")}
      </motion.h1>
      <motion.p
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...SETTLE_SPRING, delay: 0.1 }}
        className="mx-auto mt-5 max-w-xl font-body text-[15px] leading-relaxed text-home-ink/75 md:text-base"
      >
        {t("hero.subtitle")}
      </motion.p>
    </div>
  );

  if (!interactive) {
    // Static fallback: labeled image of the world + resolved activity panel.
    return (
      <section className="bg-home-cream">
        {textZone}
        <div className="mx-auto max-w-xl px-5 pb-10">
          <div
            role="img"
            aria-label={t("hero.worldAlt")}
            className="relative h-[280px] overflow-hidden rounded-[24px] shadow-[0_24px_50px_-26px_rgba(62,110,134,0.6)]"
          >
            <WorldFallback
              labels={{
                computer: t("hero.world.computer"),
                fileCabinet: t("hero.world.fileCabinet"),
                mailbox: t("hero.world.mailbox"),
              }}
            />
          </div>
          <p className="mt-3 text-center font-body text-[13px] font-medium text-home-ink/65">
            {t("hero.mascotLine")}
          </p>
          <div className="mt-5">
            <ActivityPanel t={t} instant />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-home-cream">
      {textZone}

      {/* The world: full bleed, rising to meet the text with a seamless
          blend that may overlap the last subhead lines, ending on a crisp
          line below. */}
      <div className="relative -mt-16 h-[52vh] min-h-[420px] w-full overflow-hidden border-b border-home-ink/10 md:-mt-20 md:h-[58vh] md:max-h-[720px] md:min-h-[480px]">
        <div className="absolute inset-0 bg-home-water" aria-hidden="true">
          <HeroWorld
            onHover={handleHover}
            onClawbertTap={() => setPanelKey((k) => k + 1)}
            clawbertCaption={t("hero.mascotLine")}
          />
        </div>

        {/* Seamless blend from the off-white text zone into the scene.
            Solid at the top so any overlapping subhead lines stay legible. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-[52%] bg-gradient-to-b from-home-cream from-18% to-[#F8F6F200]"
        />

        {/* The activity panel: floating card on desktop */}
        {isDesktop && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...SETTLE_SPRING, delay: 0.5 }}
            className="absolute right-8 bottom-8 z-10 w-[330px] md:right-12 md:bottom-10"
          >
            <ActivityPanel key={panelKey} t={t} instant={false} />
          </motion.div>
        )}
      </div>

      {/* On phones the panel stacks below the world as a full-width card */}
      {!isDesktop && (
        <div className="px-5 pt-4 pb-8">
          <ActivityPanel key={panelKey} t={t} instant={false} />
        </div>
      )}

      {/* Hover tooltip */}
      <AnimatePresence>
        {tooltip && (
          <motion.span
            key={tooltip.id}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.15 }}
            className="pointer-events-none fixed z-50 rounded-full bg-home-ink/90 px-3.5 py-1.5 font-body text-[13px] font-bold whitespace-nowrap text-home-cream"
            style={{ left: tooltip.x + 14, top: tooltip.y - 12 }}
          >
            {t(`hero.world.${tooltip.id}`)}
          </motion.span>
        )}
      </AnimatePresence>
    </section>
  );
}
